/**
 * Utilitários para manipulação de dados de subscription/plano
 *
 * O backend retorna dados de plano em diferentes formatos dependendo do endpoint:
 *
 * 1. PaymentSubscription serializer (/payment_subscriptions/*):
 *    - attributes.paymentPlan.name - nome do plano
 *    - attributes.paymentPlan.id - id do plano
 *
 * 2. Partner serializer (currentPlan):
 *    - currentPlan.name - nome do plano (objeto PaymentPlan)
 *
 * 3. PaymentPlan serializer (/payment_plans):
 *    - attributes.name - nome do plano
 */

const DEFAULT_PLAN_NAME = "Plano Gratuito";

/**
 * Planos ocultos temporariamente na vitrine do front (seguem ativos no
 * backend/GalaxPay — decisão 10/08/2026, ainda sem apresentação definida).
 * IDs de payment_plans em produção: 55 = Mensageria Whatsapp, 56 = Mensageria Enterprise.
 */
export const HIDDEN_PLAN_IDS = [55, 56];

/**
 * Verifica se um plano deve ficar oculto na vitrine (ver HIDDEN_PLAN_IDS).
 *
 * @param {number|string|Object} planIdOrItem - id do plano, ou item cru da API (com .id)
 * @returns {boolean} True se o plano deve ser escondido
 */
export const isHiddenPlan = (planIdOrItem) => {
  const id =
    typeof planIdOrItem === "object" && planIdOrItem !== null ? planIdOrItem.id : planIdOrItem;
  return HIDDEN_PLAN_IDS.includes(Number(id));
};

/**
 * Extrai o nome do plano de um objeto subscription (PaymentSubscription attributes)
 *
 * @param {Object} subscriptionAttributes - Objeto attributes do PaymentSubscription
 * @param {string} fallback - Valor padrão se não encontrar (default: "Plano Gratuito")
 * @returns {string} Nome do plano
 *
 * @example
 * // Uso com resposta da API /payment_subscriptions/last_active_subscription
 * const { data } = await api.get('/partners/payment_subscriptions/last_active_subscription');
 * const planName = getPlanNameFromSubscription(data?.data?.attributes);
 */
export const getPlanNameFromSubscription = (
  subscriptionAttributes,
  fallback = DEFAULT_PLAN_NAME,
) => {
  if (!subscriptionAttributes) return fallback;

  // O PaymentSubscriptionSerializer retorna paymentPlan como objeto
  return subscriptionAttributes.paymentPlan?.name || fallback;
};

/**
 * Extrai o nome do plano de um objeto PaymentPlan diretamente
 *
 * @param {Object} paymentPlan - Objeto PaymentPlan (pode ser attributes ou objeto direto)
 * @param {string} fallback - Valor padrão se não encontrar (default: "Plano Gratuito")
 * @returns {string} Nome do plano
 *
 * @example
 * // Uso com currentPlan do user (AuthContext)
 * const planName = getPlanNameFromPlan(user?.currentPlan);
 *
 * // Uso com item da lista de payment_plans
 * const planName = getPlanNameFromPlan(item.attributes);
 */
export const getPlanNameFromPlan = (paymentPlan, fallback = DEFAULT_PLAN_NAME) => {
  if (!paymentPlan) return fallback;

  // Pode ser objeto direto (name) ou attributes (attributes.name)
  return paymentPlan.name || paymentPlan.attributes?.name || fallback;
};

/**
 * Verifica se o plano é gratuito baseado no nome
 *
 * @param {string} planName - Nome do plano
 * @returns {boolean} True se for plano gratuito
 */
export const isFreePlan = (planName) => {
  if (!planName) return true;
  return planName === DEFAULT_PLAN_NAME || planName.toLowerCase().includes("gratuito");
};

/**
 * Verifica se o plano é do tipo boleto baseado no nome
 *
 * @param {string} planName - Nome do plano
 * @returns {boolean} True se for plano boleto
 */
export const isBoleto = (planName) => {
  if (!planName) return false;
  return planName.toLowerCase().includes("boleto");
};

/**
 * Retorna informações de status do plano para exibição
 *
 * @param {string} planName - Nome do plano
 * @returns {Object} Objeto com status, color e variant para Badge/UI
 */
export const getPlanStatus = (planName) => {
  if (!planName) {
    return { status: "inactive", color: "slate", variant: "outline", label: "Sem plano" };
  }

  if (isFreePlan(planName)) {
    return { status: "free", color: "blue", variant: "secondary", label: "Gratuito" };
  }

  if (isBoleto(planName)) {
    return { status: "boleto", color: "amber", variant: "default", label: "Boleto" };
  }

  return { status: "active", color: "emerald", variant: "default", label: "Ativo" };
};

export default {
  getPlanNameFromSubscription,
  getPlanNameFromPlan,
  isFreePlan,
  isBoleto,
  getPlanStatus,
  isHiddenPlan,
  HIDDEN_PLAN_IDS,
  DEFAULT_PLAN_NAME,
};
